// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo } from 'react';

import { type MachineElement, type MachineProfile, useEvolvePaginatedGet, useEvolvePatch } from '@evolvemep/api-ts';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { FormModal, useFormModal } from 'components/Mantine/Modals/FormModal';
import { SearchableSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { BenderProfileOption } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BenderProfileOption';
import { useConduits } from 'modules/Shop/EvolveFabrication/useConduits';

type Props = {
  /** The element whose bender profile is being changed. When nil, the modal is closed. */
  element: MachineElement | null;
  onClose: () => void;
  /** Called with the updated element after its bender profile is changed. */
  onProfileChanged: (updatedElement: MachineElement) => void;
};

type FormValues = {
  benderProfile: MachineProfile | null;
};

const ChangeBenderProfileForm = ({
  element,
  onProfileChanged,
}: {
  element: MachineElement;
} & Pick<Props, 'onProfileChanged'>) => {
  const { conduitTypes, conduitSizes } = useConduits();
  const { apiCall: changeBenderProfileApiCall } = useEvolvePatch('shop/fabrication/element/:elementId/benderProfile');

  const defaultOpts = useMemo(
    () => ({
      defaultConfig: {
        params: {
          conduitTypeId: conduitTypes?.find((ct) => ct.name === element.materialType)?.conduitTypeId,
          conduitSizeId: conduitSizes?.find((cs) => cs.name === element.materialSize)?.conduitSizeId,
        },
      },
    }),
    [conduitTypes, conduitSizes, element.materialType, element.materialSize],
  );

  const paginatedProfiles = useEvolvePaginatedGet('shop/fabrication/machineProfile', defaultOpts);

  const { setDefaultOpts } = paginatedProfiles;
  useEffect(() => {
    setDefaultOpts(defaultOpts);
  }, [defaultOpts, setDefaultOpts]);

  const form = useForm<FormValues>({
    initialValues: { benderProfile: null },
    validate: { benderProfile: (value) => isNil(value) },
  });

  const onSubmit = async ({ benderProfile }: typeof form.values) => {
    if (isNil(benderProfile)) return;
    const updatedElement = await changeBenderProfileApiCall(
      { benderProfileId: benderProfile.machineProfileId },
      { url: `shop/fabrication/element/${element.elementId}/benderProfile` },
    );
    onProfileChanged(updatedElement);
    notifications.show({
      title: 'Successfully changed bender profile',
      message: `${element.name} has been assigned to ${benderProfile.name}.`,
      color: 'green',
    });
  };

  useFormModal(form, onSubmit);

  return (
    <SearchableSelect
      paginatedGet={paginatedProfiles}
      idKey="machineProfileId"
      label="Bender Profile"
      getItemLabel={({ name }) => name}
      renderOption={BenderProfileOption}
      required
      placeholder="Select a bender"
      {...form.getInputProps('benderProfile')}
    />
  );
};

/**
 * Modal that lets the user assign a different bender profile to an individual bend.
 * The dropdown only lists profiles applicable to the bend's conduit type/size.
 */
export const ChangeBenderProfileModal = ({ element, onClose, onProfileChanged }: Props) => (
  <FormModal
    title="Change bender profile"
    opened={isNotNil(element)}
    onClose={onClose}
    type="submit"
    confirmationText="Save"
    size="sm"
  >
    {isNotNil(element) && (
      <ChangeBenderProfileForm key={element.elementId} element={element} onProfileChanged={onProfileChanged} />
    )}
  </FormModal>
);
